// @ts-nocheck
/* eslint-disable */
import React, { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { Grid, Hidden, makeStyles, Tab, Tabs, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';

import {
  ACCESS_KEY,
  CONNECTION_SUCCESS_MESSAGE,
  FACEBOOK_LIMIT,
  LINKEDIN_LIMIT,
  TWITTER_LIMIT,
} from 'app-constants';
import { MentionsEditor, PaddedPaper, SocialIcon, ValidatedTextFieldNext } from 'components';
import {
  CreatePublicShareInfoMutation,
  ListPublicSocialConnectionsQuery,
  Network,
  ShareContentDestination,
  ShareType,
  useCreatePublicShareInfoMutation,
  useGetClientReleaseTogglesPublicQuery,
  useListPublicSocialConnectionsQuery,
  usePublicShareContentMutation,
} from 'api/public';
import { getSocialAccountName } from 'helpers';
import { useContentVariations, useGlobalClient, useNotify, useSharePhoto } from 'hooks';
import { popup } from 'utilities';
import { TextCounter } from 'features/posting/components/Partials';
import { getCommentCharLimit } from 'features/posting/helpers';

import { usePublicPostContext } from '../PublicPostContext';
import PublicShareButton from './PublicShareButton';
import PublicShareErrorMessage from './PublicShareErrorMessage';
import PublicShareSuccessMessage from './PublicShareSuccessMessage';
import { useQueryClient } from 'react-query';

const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(2),
  },
  textbox: {
    backgroundColor: theme.palette.common.white,
  },
  hasShared: {
    opacity: 0.4,
  },
  socialLabel: {
    marginLeft: theme.spacing(0.5),
  },
  indicator: {
    display: 'none',
  },
  tabs: {
    minHeight: theme.spacing(2),
    padding: theme.spacing(1, 0, 1, 2),
    background: theme.palette.grey[100],
  },
  tab: {
    fontSize: theme.spacing(1.7),
    minWidth: theme.spacing(3),
    padding: theme.spacing(0, 2.5, 0, 0),
    minHeight: theme.spacing(2),
  },
  textField: {
    width: '100%',
  },
}));

type IProps = {
  network: Network;
};

const PublicShareTextBoxV2: FC<IProps> = ({ network }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const notify = useNotify();
  const variations = useContentVariations();
  const { selectedPhotoIndex } = useSharePhoto();
  const shareCopies = variations[`${network}Commentary`];
  const selectedShareCopyIndex = variations[`${network}CommentaryIndex`];

  const { state } = usePublicPostContext();
  const { content } = state;
  // photos may be missing
  const selectedPhoto = (content.content_object?.photos || [])[selectedPhotoIndex];
  const lock_commentary = content?.lock_commentary;
  const content_id = content?.content_id;

  const [message, setMessage] = useState(shareCopies[selectedShareCopyIndex]?.commentary);
  const [hasShared, setHasShared] = useState(false);
  const queryClient = useQueryClient();
  // we use this flag to trigger a forced share after a connection (Since the user already hit the share button)
  const [isForceShare, setIsForceShare] = useState(false);

  const client = useGlobalClient();
  const publicReleaseToggles = useGetClientReleaseTogglesPublicQuery(
    {
      client_id: client.id,
    },
    {
      onError: notify.queryError,
    }
  );
  const hasLinkedInMentions =
    publicReleaseToggles?.data?.getClientReleaseToggles.includes('linkedinCompanyMentions');

  const shouldShowMentionsEditor = network === Network.Linkedin && hasLinkedInMentions;
  const limit = getCommentCharLimit(network, content);
  const accessKey = localStorage.getItem(ACCESS_KEY);
  const networkProperties = {
    [Network.Twitter]: {
      limit: TWITTER_LIMIT,
      title: t('admin:twitter'),
      placeholder: t('posting:twitterShare'),
    },
    [Network.Linkedin]: {
      limit: LINKEDIN_LIMIT,
      title: t('admin:linkedin'),
      placeholder: t('posting:linkedinShare'),
    },
    [Network.Facebook]: {
      limit: FACEBOOK_LIMIT,
      title: t('admin:facebook'),
      placeholder: t('posting:facebookShare'),
    },
  };

  const variables = accessKey !== 'null' ? { access_key: accessKey } : {};
  const { data } = useListPublicSocialConnectionsQuery(variables, {
    onError: notify.queryError,
    onSuccess: onListSocialSuccess,
  });

  const { mutate, isLoading } = usePublicShareContentMutation({
    onError: onShareError,
    onSuccess: onShareSuccess,
  });

  const { mutate: createShareInfoMutate, isLoading: isCreateShareInfoLoading } =
    useCreatePublicShareInfoMutation({
      onError: onShareError,
      onSuccess: onCreateShareInfoSuccess,
    });

  const loading = isLoading || isCreateShareInfoLoading;
  const items = data?.listPublicSocialConnections?.items || [];
  const userNetworkInfo = items.find(item => item.provider === network);
  const networkInfo = networkProperties[network];
  const title = getSocialAccountName(userNetworkInfo) || networkInfo.title;

  useEffect(() => {
    window.addEventListener('message', event => void onMessage(event), false);
    return () => {
      window.removeEventListener('message', event => void onMessage(event), false);
    };
  }, []);

  useEffect(() => {
    if (isForceShare && network === Network.Twitter) {
      onClickShareToTwitter();
      setIsForceShare(false);
    }
  }, [isForceShare]);

  useEffect(() => {
    setMessage(shareCopies[selectedShareCopyIndex]?.commentary);
  }, [selectedShareCopyIndex]);

  function onMessage(event: MessageEvent) {
    if (!event.data) {
      return;
    }
    const { network: messageNetwork, content_id: messageContentId, message } = event.data;
    if (
      network == messageNetwork &&
      content_id == messageContentId &&
      message == CONNECTION_SUCCESS_MESSAGE
    ) {
      const access_key = localStorage.getItem(ACCESS_KEY);
      queryClient.fetchQuery(useListPublicSocialConnectionsQuery.getKey({ access_key })).then(
        () => setIsForceShare(true),
        error => {
          console.log('Error', error);
        }
      );
    }
  }

  function onListSocialSuccess(data: ListPublicSocialConnectionsQuery) {
    const { listPublicSocialConnections } = data;
    const { access_key } = listPublicSocialConnections;

    localStorage.setItem(ACCESS_KEY, access_key);
  }

  function onShareSuccess() {
    setHasShared(true);
    notify.info({ component: <PublicShareSuccessMessage network={network} />, duration: 8000 });
  }

  function onShareError() {
    notify.error({ component: <PublicShareErrorMessage /> });
  }

  function onCreateShareInfoSuccess(data: CreatePublicShareInfoMutation) {
    const short_link = data.createPublicShareInfo?.shortened_urls?.facebook?.short_link;
    const { content_object } = content;
    const { url } = content_object;

    const href = short_link || url || window?.location?.href;

    window?.FB?.ui(
      {
        href,
        method: 'share',
        quote: message,
      },
      () => {
        const variables = {
          access_key: accessKey,
          content_id,
          items: [
            {
              share_type: ShareType.Frontend,
              destination: ShareContentDestination.Facebook,
              photos: selectedPhoto ? [selectedPhoto] : [],
            },
          ],
        };

        mutate(variables);
      }
    );
  }

  function connectNetwork() {
    popup(`connect/${network}/${content.content_id}`, 'Connection Popup', 650, 650);
  }

  function onChangeTab(event: React.SyntheticEvent, nextValue: number) {
    variations.setShareCopyIndex(nextValue, network);
  }

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    setMessage(event.target.value);
  }

  function onClickShareToLinkedin(values) {
    if (userNetworkInfo) {
      return mutate({
        content_id,
        access_key: accessKey,
        items: [
          {
            network_id: userNetworkInfo.id,
            user_commentary: values.messages[selectedShareCopyIndex].commentary,
            share_type: ShareType.Now,
            photos: selectedPhoto ? [selectedPhoto] : [],
            mentions: values.messages[selectedShareCopyIndex].mentions,
          },
        ],
      });
    }

    connectNetwork();
  }

  function onClickShareToTwitter() {
    if (userNetworkInfo) {
      return mutate({
        content_id,
        access_key: accessKey,
        items: [
          {
            network_id: userNetworkInfo.id,
            user_commentary: message,
            share_type: ShareType.Now,
            photos: selectedPhoto ? [selectedPhoto] : [],
          },
        ],
      });
    }

    connectNetwork();
  }

  function onClickShareToFacebook() {
    createShareInfoMutate({
      access_key: accessKey,
      content_id,
    });
  }

  function onClickShare(values) {
    if (network === Network.Facebook) {
      return onClickShareToFacebook();
    }
    // Need to preserve non feature toggle functionality for now, click to share twitter
    // will service both linkedin and twitter like the previous function
    if (!hasLinkedInMentions && (network === Network.Twitter || network === Network.Linkedin)) {
      onClickShareToTwitter();
    }

    if (hasLinkedInMentions && network === Network.Linkedin) {
      !!values && onClickShareToLinkedin(values);
    }

    if (hasLinkedInMentions && network === Network.Twitter) {
      onClickShareToTwitter();
    }
  }

  if (!networkInfo) {
    return null;
  }

  return (
    <Formik initialValues={{ messages: shareCopies }} onSubmit={onClickShare}>
      {({ values }) => (
        <PaddedPaper>
          <Grid container direction="column" spacing={1}>
            <Grid item xs>
              <Grid container alignItems="center" justify="space-between">
                <Grid item>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      <SocialIcon state="active" provider={network} />
                    </Grid>
                    <Grid item>
                      <Typography className={classes.socialLabel} variant="subtitle2">
                        {title}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Hidden mdUp>
                    <PublicShareButton
                      network={network}
                      hasShared={hasShared}
                      isLoading={loading}
                      isLight
                    />
                  </Hidden>
                </Grid>
              </Grid>
            </Grid>
            <Grid item container spacing={1}>
              <Grid item xs>
                <Grid direction="column" spacing={2} container>
                  <Grid item className={classes.textField}>
                    {values.messages?.length > 1 && (
                      <Tabs
                        value={selectedShareCopyIndex}
                        onChange={onChangeTab}
                        classes={{ root: classes.tabs, indicator: classes.indicator }}
                      >
                        {values.messages?.map((_: string, idx: number) => (
                          <Tab
                            key={idx}
                            label={t('sharing:option_count', { count: idx + 1 })}
                            classes={{ root: classes.tab }}
                          />
                        ))}
                      </Tabs>
                    )}
                    {shouldShowMentionsEditor ? (
                      <MentionsEditor
                        fieldName={`messages[${selectedShareCopyIndex}].commentary`}
                        mentionsFieldName={`messages[${selectedShareCopyIndex}].mentions`}
                        placeholder={t('posting:linkedinShare')}
                        publicQuery={true}
                      />
                    ) : (
                      <ValidatedTextFieldNext
                        name={`messages[${selectedShareCopyIndex}].commentary`}
                        fullWidth
                        multiline
                        rows="3"
                        rowsMax="40"
                        margin="none"
                        placeholder={networkInfo.placeholder}
                        onChange={onChange}
                        value={message}
                        className={hasShared ? classes.hasShared : ''}
                        disabled={hasShared || lock_commentary}
                      />
                    )}
                  </Grid>
                  <Grid item>
                    <TextCounter length={message?.length || 0} limit={limit} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Hidden smDown>
                  <PublicShareButton network={network} hasShared={hasShared} isLoading={loading} />
                </Hidden>
              </Grid>
            </Grid>
          </Grid>
        </PaddedPaper>
      )}
    </Formik>
  );
};

export default PublicShareTextBoxV2;
